"use client";

import {
  Box,
  Checkbox,
  Spinner,
  Stack,
  useToast,
} from "@chakra-ui/react";
import { OnboardingLayout } from "../layout/OnboardingLayout";
import { CustumSelect } from "../components/Input";
import { useState, useCallback, useRef } from "react";
import SingleCertUpload from "./cert/SingleCertUpload";
import ViewCert from "./cert/ViewCert";
import { CustomButton } from "../components/Buttons";
import { API_BASE_URL } from "../components/APIs";
import axios from "axios";

export default function Certificate() {
  const [iNeedTraining, setINeedTraining] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [iHaveCert, setIHaveCert] = useState('');
  const toast = useToast();
  
  // Ref to track previous training value to avoid duplicate API calls
  const previousTrainingValue = useRef(iNeedTraining);

  const selected = [
    { value: "no", label: "No" },
    { value: "yes", label: "Yes" },
  ];

  // Memoized API call function
  const saveCertificatePreference = useCallback(async (needTraining) => {
    const uid = sessionStorage.getItem("uid");
    
    if (!uid) {
      toast({
        title: "Error",
        description: "User session not found. Please refresh the page.",
        position: "top",
        status: "error",
        isClosable: true,
      });
      return;
    }

    // Avoid duplicate API calls
    if (previousTrainingValue.current === needTraining) {
      return;
    }

    previousTrainingValue.current = needTraining;
    setLoading(true);

    try {
      const url = `${API_BASE_URL}/application/saveCertificate`;
      const response = await axios.post(url, {
        iNeed: needTraining,
        user_id: uid,
        iHave: 0,
      });

      if (response.data.status === "200") {
        toast({
          title: "Preferences Saved",
          description: response.data.message,
          position: "top",
          status: "success",
          isClosable: true,
        });
      } else {
        throw new Error(response.data.message || "Failed to save preferences");
      }
    } catch (error) {
      toast({
        title: "Save Error",
        description: error.message,
        position: "top",
        status: "error",
        isClosable: true,
      });
      // Revert on error
      previousTrainingValue.current = !needTraining;
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // Handle checkbox change with immediate API call
  const handleTrainingChange = useCallback((event) => {
    const newValue = event.target.checked;
    setINeedTraining(newValue);
    saveCertificatePreference(newValue);
  }, [saveCertificatePreference]);

  // Handle manual save button click
  const handleManualSave = useCallback(() => {
    saveCertificatePreference(iNeedTraining);
  }, [iNeedTraining, saveCertificatePreference]);

  // Handle certificate selection change
  const handleCertificateSelection = useCallback((event) => {
    const value = event.target.value;
    setIHaveCert(value);
    
    // If user selects "yes", automatically set training to false
    if (value === "yes") {
      setINeedTraining(false);
      previousTrainingValue.current = false;
    }
  }, []);

  return (
    <OnboardingLayout
      pageTitle="YOUR TRAINING CERTIFICATES"
      mainContent={
        <Stack gap={4} direction={{ base: "column", md: "column" }}>
          <Stack gap={2} direction={["column", "row"]}>
            <CustumSelect
              value={iHaveCert}
              onChange={handleCertificateSelection}
              name="iHaveCert"
              placeholder="Select"
              formLabel="Do you have training certificate(s)?"
              options={selected}
            />
          </Stack>

          {iHaveCert === "no" && (
            <>
              <Checkbox
                isChecked={iNeedTraining}
                onChange={handleTrainingChange}
                isDisabled={isLoading}
              >
                I Need Training
              </Checkbox>
              
              <Stack gap={2} direction={["column", "column"]}>
                <Box py={3}>
                  {isLoading ? (
                    <Spinner
                      thickness="3px"
                      speed="0.65s"
                      emptyColor="gray.200"
                      color="blue.500"
                      size="md"
                    />
                  ) : (
                    <CustomButton
                      onClick={handleManualSave}
                      bg={"blue.400"}
                      colorScheme="blue"
                      label="Save Preferences"
                      isDisabled={isLoading}
                    />
                  )}
                </Box>
              </Stack>
            </>
          )}
          
          {iHaveCert === "yes" && <SingleCertUpload />}

          <Box
            w={{ base: "90%", md: "50%" }}
            mx={"auto"}
            p={3}
            border={"2px solid #012"}
          >
            <ViewCert />
          </Box>
        </Stack>
      }
    />
  );
}